import { Component, HostBinding } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RenderFailureBlame } from '../render-flow.model';
import { RenderFlowService } from '../render-flow.service';
import { RenderHelpService } from '../render-help.service';
import { formatBytes } from '../render-upload.inspector';
import { RENDER_PREVIEW_STAGES, RENDER_PRICE_USD, RENDER_SUPPORT_EMAIL, renderSampleById } from '../render.data';
import { RenderStepBase } from './render-step-base';

// A line of the page: its text, and the model's name in it where it says one — with the mark that
// holds on to the name, and what follows.
interface RenderErrorLine {
  text: string;
  name?: string;
  mark?: string;
  after?: string;
}

// A preview or the videos failed (proposals 2026-09-30). Shown in the layout of the step that was
// awaited — the devices on the left, the stages card on the right with the failed stage and its
// reason. Nothing here starts a render again (owner, 2026-10-20): a model is rendered once, as
// uploaded. A failed preview offers the upload ("Upload the fixed model" when the model is to blame,
// "Try again" otherwise) and support; failed videos, paid for, offer support only.
// Also where a RenderNotImplementedError from the live stub surfaces.
@Component({
  selector: 'app-render-error-step',
  templateUrl: './render-error-step.component.html',
})
export class RenderErrorStepComponent extends RenderStepBase {
  @HostBinding('class') public readonly hostClass = 'render-step step-error';

  public readonly stages = RENDER_PREVIEW_STAGES;

  constructor(route: ActivatedRoute, router: Router, flow: RenderFlowService, private readonly help: RenderHelpService) {
    super(route, router, flow);
  }

  // The renderer could not read the textures: the export guide, at the step that packs them in,
  // for the program the FBX came from when it said.
  public openGuide(): void {
    const source = this.job?.source;
    this.help.open({ exporter: source?.kind === 'upload' ? source.exporter : undefined, step: 'pack' });
  }

  public get isMock(): boolean {
    return this.flow.isMock;
  }

  public get isVideos(): boolean {
    return this.job?.error?.failedStep === 'rendering';
  }

  public get blame(): RenderFailureBlame {
    return this.job?.error?.blame || 'service';
  }

  // The title and the subtitle, around the model's name where they say it: the page puts the name
  // on one line of its own (`.rf-name`).
  public get title(): RenderErrorLine {
    if (this.isVideos) {
      const progress = this.job?.error?.progress;
      return { text: progress ? `Your videos stopped at ${progress}%.` : 'Your videos stopped.' };
    }
    switch (this.blame) {
      case 'model':
        return this.job?.name ? { text: 'We couldn’t render ', name: this.job.name, mark: '.' } : { text: 'We couldn’t render your model.' };
      case 'account':
        return { text: 'We couldn’t start this preview.' };
      default:
        return { text: 'The renderer stopped.' };
    }
  }

  public get subtitle(): RenderErrorLine {
    if (this.isVideos) {
      return { text: 'Your payment is safe. Contact support and we’ll sort it out.' };
    }
    switch (this.blame) {
      case 'model':
        return { text: 'Something in the model’s files stopped the renderer. Fix it, then upload the model again.' };
      case 'account':
        return { text: 'Your account can’t start a render right now.' };
      default:
        return this.job?.name
          ? { text: 'Nothing is wrong with ', name: this.job.name, mark: '.', after: ' Upload it again: previews are free.' }
          : { text: 'Nothing is wrong with your model. Upload it again: previews are free.' };
    }
  }

  // The stage the run failed in. A model the renderer could not read (or whose textures it could
  // not find) failed its check — the stage that exists to catch it (decision 40), wherever the
  // progress stood. Anything else stopped where its progress had reached (the last stage when
  // unknown): a GPU job that never started is the studio, never the model.
  public stageFate(index: number): 'done' | 'failed' | 'pending' {
    const progress = this.job?.error?.progress;
    let failed = this.stages.length - 1;
    if (this.blame === 'model') {
      failed = 1;
    } else if (progress !== undefined) {
      failed = this.stages.reduce((current, stage, i) => (progress >= stage.from ? i : current), 0);
    }
    if (index < failed) {
      return 'done';
    }
    return index === failed ? 'failed' : 'pending';
  }

  public get uploadedDetail(): string {
    const source = this.job?.source;
    if (!source) {
      return '';
    }
    const bytes = source.kind === 'upload' ? source.totalBytes : renderSampleById(source.sampleId)?.fileBytes;
    return bytes ? formatBytes(bytes) : '';
  }

  public get paidLabel(): string {
    return `Paid · $${RENDER_PRICE_USD}.00`;
  }

  public get stoppedLabel(): string {
    const progress = this.job?.error?.progress;
    return progress ? `Stopped at ${progress}%` : 'Stopped';
  }

  // A failed preview's button: both go to the upload.
  public get primaryLabel(): string {
    return this.blame === 'model' ? 'Upload the fixed model' : 'Try again';
  }

  public get fine(): string {
    return this.isVideos ? 'You paid once. You won’t be charged again.' : 'Previews are free. Uploading again costs nothing.';
  }

  public get supportHref(): string {
    const what = this.isVideos ? 'Videos stopped' : 'Preview failed';
    const subject = encodeURIComponent(`${what} · ${this.job?.name || ''} · ${this.job?.modelId || ''}`);
    return `mailto:${RENDER_SUPPORT_EMAIL}?subject=${subject}`;
  }

  // "Remove from library": a failed preview only, and only after a second click that names it.
  public confirmingRemove = false;

  public get canRemove(): boolean {
    return !!this.job && this.flow.canRemove(this.job.modelId);
  }

  public onRemove(): void {
    if (!this.job) {
      return;
    }
    this.flow.removeFailed(this.job.modelId);
    this.router.navigateByUrl(RenderFlowService.ownJobs(this.flow.jobs).length ? '/render/library' : '/render/upload');
  }
}
